// src/pages/UserStatsDashboard.jsx — UI Pro Moderne v2
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Stack, Typography, Chip, Alert, CircularProgress,
  Button, TextField, MenuItem, IconButton, Tooltip,
  LinearProgress, Divider,
  Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import RefreshIcon        from "@mui/icons-material/Refresh";
import CalendarTodayIcon  from "@mui/icons-material/CalendarToday";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";
import CancelIcon         from "@mui/icons-material/Cancel";
import TimerIcon          from "@mui/icons-material/Timer";
import TrendingDownIcon   from "@mui/icons-material/TrendingDown";
import TrendingUpIcon     from "@mui/icons-material/TrendingUp";
import FilterListIcon     from "@mui/icons-material/FilterList";
import DownloadIcon       from "@mui/icons-material/Download";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import FlashOnIcon        from "@mui/icons-material/FlashOn";
import SchoolIcon         from "@mui/icons-material/School";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useNavigate }    from "react-router-dom";
import api, { getUserMessage } from "../api/axios";
import { pageWrap, minutesFmt } from "../utils/pageStyles";

// ── Helpers ────────────────────────────────────────────────────────────────
const fmt = (n) => (n ?? 0).toLocaleString("fr");
const daysOptions   = [7, 14, 30, 60, 90];
const statusOptions = ["approved","running","started","completed","cancelled"];
const PIE_COLORS    = ["#22c55e", "#ef4444", "#f59e0b"];

const mapMsgs = (t, data, key) => {
  if (!data) return [];
  const codes = Array.isArray(data[`${key}_codes`]) ? data[`${key}_codes`] : [];
  return codes.length > 0
    ? codes.map(c => t(`${key}s.${c}`, { defaultValue: c }))
    : (Array.isArray(data[`${key}s`]) ? data[`${key}s`] : []);
};

// ── Composants réutilisables ───────────────────────────────────────────────
function SectionCard({ title, subtitle, children, loading, action, icon }) {
  return (
    <Paper elevation={0} sx={{
      border: "1px solid", borderColor: "divider", borderRadius: 3,
      overflow: "hidden", mb: 2,
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {icon && <Box sx={{ color: "text.secondary" }}>{icon}</Box>}
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {loading && <CircularProgress size={14} />}
          {action}
        </Stack>
      </Stack>
      <Box sx={{ p: 3 }}>{children}</Box>
    </Paper>
  );
}

function KpiCard({ label, value, sub, icon, color = "#6366f1", bg = "#eef2ff", progress, highlight }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, minWidth: 150, p: 2.5, borderRadius: 3,
      border: "1.5px solid", borderColor: highlight ? color + "60" : "divider",
      bgcolor: highlight ? bg : "background.paper",
      transition: "box-shadow .2s",
      "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,.07)" },
    }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: bg,
          display: "flex", alignItems: "center", justifyContent: "center", color, flexShrink: 0 }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700} lineHeight={1}>{value}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: 12 }}>{label}</Typography>
          {sub && <Typography variant="caption" sx={{ color, fontSize: 11 }}>{sub}</Typography>}
        </Box>
      </Stack>
      {progress != null && (
        <LinearProgress variant="determinate" value={progress}
          sx={{ mt: 1.5, height: 5, borderRadius: 3, bgcolor: "#f1f5f9",
            "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 3 } }} />
      )}
    </Paper>
  );
}

function EmptyChart({ text }) {
  return (
    <Box sx={{ height: 200, display: "flex", alignItems: "center",
      justifyContent: "center", bgcolor: "#f8fafc", borderRadius: 2 }}>
      <Typography variant="body2" color="text.secondary">{text || "Aucune donnée"}</Typography>
    </Box>
  );
}

// ── Page principale ────────────────────────────────────────────────────────
export default function UserStatsDashboard() {
  const { t }    = useTranslation();
  const navigate = useNavigate();
  const tr = (key, fallback) => { const v = t(key); return v === key ? fallback : v; };

  // Filtres
  const [days,         setDays]         = useState(30);
  const [labId,        setLabId]        = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom,     setDateFrom]     = useState("");
  const [dateTo,       setDateTo]       = useState("");
  const [showFilters,  setShowFilters]  = useState(false);

  // Données
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [overview,      setOverview]      = useState(null);
  const [series,        setSeries]        = useState([]);
  const [topLabs,       setTopLabs]       = useState([]);
  const [activity,      setActivity]      = useState([]);
  const [resStats,      setResStats]      = useState(null);
  const [nextHopPacks,  setNextHopPacks]  = useState([]);
  const [nhError,       setNhError]       = useState("");
  const [labsOptions,   setLabsOptions]   = useState([]);
  const [quota,         setQuota]         = useState(null);
  const [quotaLoading,  setQuotaLoading]  = useState(false);

  // Pagination activité
  const [actPage, setActPage] = useState(0);
  const ACT_PAGE_SIZE = 10;

  // Pro-check
  const [proStart,   setProStart]   = useState("");
  const [proEnd,     setProEnd]     = useState("");
  const [proLoading, setProLoading] = useState(false);
  const [proError,   setProError]   = useState("");
  const [proResult,  setProResult]  = useState(null);

  const baseURL = api?.defaults?.baseURL || "/api/v1";
  const filtersActive = !!(labId || statusFilter || dateFrom || dateTo);

  // Charger les labs une fois
  useEffect(() => {
    api.get("/labs").then(r => {
      const d = r.data;
      setLabsOptions(Array.isArray(d) ? d : d?.items || []);
    }).catch(() => {});
  }, []);

  // Charger les quotas quand un lab est sélectionné
  useEffect(() => {
    if (!labId) { setQuota(null); return; }
    setQuotaLoading(true);
    api.get(`/labs/${labId}`)
      .then(r => setQuota(r.data || null))
      .catch(() => setQuota(null))
      .finally(() => setQuotaLoading(false));
  }, [labId]);

  // Charger les stats
  const loadData = useCallback(async () => {
    setLoading(true); setError("");
    const lp = labId        ? `&lab_id=${labId}`                         : "";
    const sp = statusFilter ? `&status=${encodeURIComponent(statusFilter)}` : "";
    const fp = dateFrom     ? `&date_from=${dateFrom}`                   : "";
    const tp = dateTo       ? `&date_to=${dateTo}`                       : "";

    try {
      const [ovRes, tsRes, tlRes, raRes, rsRes, nhRes] = await Promise.allSettled([
        api.get("/user/stats/overview"),
        api.get(`/user/stats/timeseries?days=${days}${lp}${sp}${fp}${tp}`),
        api.get(`/user/stats/top-labs?days=${days}&limit=10${lp}${sp}${fp}${tp}`),
        api.get(`/user/stats/recent-activity?limit=100${lp}${fp}${tp}`),
        api.get("/reservations/stats/me"),
        api.get("/stats/me/next-hop-packs"),
      ]);
      if (ovRes.status === "fulfilled") setOverview(ovRes.value.data || null);
      if (tsRes.status === "fulfilled") setSeries(tsRes.value.data?.items || []);
      if (tlRes.status === "fulfilled") setTopLabs(tlRes.value.data?.items || []);
      if (raRes.status === "fulfilled") setActivity(raRes.value.data?.items || []);
      if (rsRes.status === "fulfilled") setResStats(rsRes.value.data || null);
      if (nhRes.status === "fulfilled") {
        const d = nhRes.value.data;
        setNextHopPacks(Array.isArray(d) ? d : d?.items || []);
        setNhError("");
      } else if (nhRes.reason?.response?.status !== 404) {
        setNhError(getUserMessage(nhRes.reason, ""));
      }
    } catch (e) {
      setError(getUserMessage(e, t("common.unknownError")));
    } finally { setLoading(false); }
  }, [days, labId, statusFilter, dateFrom, dateTo, t]);

  useEffect(() => { loadData(); }, [loadData]);


  // Pro-check
  const handleProCheck = async () => {
    if (!labId) return setProError(t("userStats.proCheck.errors.noLab"));
    if (!proStart || !proEnd) return setProError(t("userStats.proCheck.errors.missingDates"));
    setProLoading(true); setProError(""); setProResult(null);
    try {
      const r = await api.post("/reservations/pro-check",
        { lab_id: Number(labId), start_time: proStart, end_time: proEnd });
      setProResult(r.data);
    } catch (e) { setProError(getUserMessage(e, t("common.unknownError"))); }
    finally { setProLoading(false); }
  };

  // Calculs
  const kpi             = overview?.kpi || {};
  const totalRes        = resStats?.total_reservations || 0;
  const totalCompleted  = resStats?.total_completed    || 0;
  const totalCancelled  = resStats?.total_cancelled    || 0;
  const completionRate  = totalRes > 0 ? Math.round((totalCompleted / totalRes) * 100) : 0;
  const totalDebited    = resStats?.total_debited_minutes   || 0;
  const totalRefunded   = resStats?.total_refunded_minutes  || 0;
  const netConsumed     = resStats?.net_consumed_minutes    || 0;

  const completionPieData = useMemo(() => {
    if (!resStats || totalRes === 0) return [];
    return [
      totalCompleted > 0 && { name: "Complétées", value: totalCompleted, key: "completed" },
      totalCancelled > 0 && { name: "Annulées",   value: totalCancelled, key: "cancelled" },
      Math.max(totalRes - totalCompleted - totalCancelled, 0) > 0 && {
        name: "Autres", value: totalRes - totalCompleted - totalCancelled, key: "other" },
    ].filter(Boolean);
  }, [resStats, totalRes, totalCompleted, totalCancelled]);

  const nhTotal = (nextHopPacks || []).reduce(
    (s, x) => s + Number(x?.minutes ?? x?.change_minutes ?? 0), 0
  );

  const fmtQuota = v => (v == null ? t("common.unlimited") : fmt(v));

  return (
    <Box sx={pageWrap}>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <Stack direction={{ xs:"column", sm:"row" }} alignItems={{ sm:"center" }}
        justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{t("userStats.title")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Statistiques personnelles · {days} derniers jours
            {filtersActive && <Chip size="small" label="Filtres actifs" sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: "#eef2ff", color: "#6366f1" }} />}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <TextField select size="small" value={days} onChange={e => setDays(Number(e.target.value))}
            sx={{ minWidth: 130, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
            {daysOptions.map(d => (
              <MenuItem key={d} value={d}>{d} jours</MenuItem>
            ))}
          </TextField>
          <Tooltip title="Filtres avancés">
            <IconButton size="small" onClick={() => setShowFilters(v => !v)}
              sx={{ border: "1px solid", borderColor: filtersActive ? "#6366f1" : "divider",
                borderRadius: 2, color: filtersActive ? "#6366f1" : "inherit" }}>
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common.refresh")}>
            <IconButton size="small" onClick={loadData} disabled={loading}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
            onClick={() => window.open(`${baseURL}/user/stats/export.csv?days=${days}`, "_blank")}
            sx={{ borderRadius: 2, fontSize: 12 }}>
            CSV
          </Button>
          <Button size="small" variant="contained" startIcon={<AccountBalanceWalletIcon />}
            onClick={() => navigate("/user/credits")}
            sx={{ borderRadius: 2, fontSize: 12, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
            Mes crédits
          </Button>
        </Stack>
      </Stack>

      {/* ── Filtres avancés ────────────────────────────────────────── */}
      {showFilters && (
        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider",
          borderRadius: 3, p: 2.5, mb: 2 }}>
          <Stack direction={{ xs:"column", sm:"row" }} spacing={1.5} flexWrap="wrap" alignItems="center">
            <TextField select size="small" label="Lab" value={labId}
              onChange={e => setLabId(e.target.value)}
              sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
              <MenuItem value="">Tous les labs</MenuItem>
              {labsOptions.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Statut" value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
              <MenuItem value="">Tous</MenuItem>
              {statusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField type="date" size="small" label="Du" InputLabelProps={{ shrink: true }}
              value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <TextField type="date" size="small" label="Au" InputLabelProps={{ shrink: true }}
              value={dateTo} onChange={e => setDateTo(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            {filtersActive && (
              <Button size="small" variant="text" onClick={() => {
                setLabId(""); setStatusFilter(""); setDateFrom(""); setDateTo("");
              }} sx={{ borderRadius: 2, color: "#ef4444", fontSize: 12 }}>
                Effacer
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !overview && (
        <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {/* ── KPIs réservations ─────────────────────────────────── */}
          <Stack direction={{ xs:"column", sm:"row" }} spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
            <KpiCard label="Total réservations" value={fmt(kpi.my_reservations_total)}
              icon={<CalendarTodayIcon />} color="#6366f1" bg="#eef2ff" />
            <KpiCard label="Aujourd'hui / 7 jours"
              value={`${fmt(kpi.my_reservations_today)} / ${fmt(kpi.my_reservations_last_7d)}`}
              icon={<FlashOnIcon />} color="#0369a1" bg="#f0f9ff" />
            <KpiCard label="Ce mois" value={fmt(kpi.my_reservations_this_month)}
              icon={<TimerIcon />} color="#059669" bg="#ecfdf5" />
            <KpiCard label="Taux complétion" value={`${completionRate}%`}
              icon={<CheckCircleIcon />}
              color={completionRate >= 80 ? "#16a34a" : completionRate >= 50 ? "#d97706" : "#dc2626"}
              bg={completionRate >= 80 ? "#f0fdf4" : completionRate >= 50 ? "#fffbeb" : "#fef2f2"}
              progress={completionRate}
              sub={`${fmt(totalCompleted)} complétées · ${fmt(totalCancelled)} annulées`} />
            <KpiCard label="Minutes consommées" value={`${fmt(netConsumed)} min`}
              icon={<TrendingDownIcon />} color="#f59e0b" bg="#fffbeb"
              sub={totalRefunded > 0 ? `${fmt(totalRefunded)} min remboursées` : null} />
          </Stack>

          {/* ── Évolution réservations ──────────────────────────────── */}
          <SectionCard title="Évolution des réservations"
            subtitle={`${days} derniers jours`}
            icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}>
            {series.length === 0
              ? <EmptyChart text="Aucune donnée pour cette période" />
              : (
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94a3b8" }}
                        tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }}
                        tickLine={false} axisLine={false} />
                      <RTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0",
                        fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,.08)" }} />
                      <Line type="monotone" dataKey="reservations" name="Réservations"
                        stroke="#6366f1" strokeWidth={2.5} dot={false}
                        activeDot={{ r: 4, fill: "#6366f1" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )
            }
          </SectionCard>

          {/* ── Top labs + Pie ─────────────────────────────────────── */}
          <Stack direction={{ xs:"column", md:"row" }} spacing={2} sx={{ mb: 2 }}>

            {/* Top labs */}
            <Paper elevation={0} sx={{ flex: 2, border: "1px solid", borderColor: "divider",
              borderRadius: 3, overflow: "hidden" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between"
                sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={700}>Top labs</Typography>
                <Typography variant="caption" color="text.secondary">
                  Clic sur une barre pour filtrer
                </Typography>
              </Stack>
              <Box sx={{ p: 3 }}>
                {topLabs.length === 0
                  ? <EmptyChart text="Aucun lab utilisé sur cette période" />
                  : (
                    <Box sx={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={topLabs} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="lab_name" tick={{ fontSize: 10, fill: "#94a3b8" }}
                            tickLine={false} axisLine={false} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }}
                            tickLine={false} axisLine={false} />
                          <RTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0",
                            fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar dataKey="reservations" name="Réservations" fill="#6366f1"
                            radius={[3, 3, 0, 0]} cursor="pointer"
                            onClick={(_, i) => { const l = topLabs[i]; if (l) setLabId(l.lab_id || l.id); }} />
                          <Bar dataKey="minutes_booked" name="Minutes" fill="#c7d2fe"
                            radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )
                }
              </Box>
            </Paper>

            {/* Pie complétées vs annulées */}
            <Paper elevation={0} sx={{ flex: 1, border: "1px solid", borderColor: "divider",
              borderRadius: 3, overflow: "hidden" }}>
              <Stack sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle1" fontWeight={700}>Répartition</Typography>
                <Typography variant="caption" color="text.secondary">Clic sur une part pour filtrer</Typography>
              </Stack>
              <Box sx={{ p: 3 }}>
                {completionPieData.length === 0
                  ? <EmptyChart text="Pas assez de données" />
                  : (
                    <>
                      <Box sx={{ height: 180 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={completionPieData} dataKey="value" nameKey="name"
                              cx="50%" cy="50%" outerRadius={70} innerRadius={35}
                              paddingAngle={3}
                              onClick={(_, i) => {
                                const it = completionPieData[i];
                                setStatusFilter(it?.key === "other" ? "" : (it?.key || ""));
                              }}>
                              {completionPieData.map((_, i) => (
                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <RTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        {completionPieData.map((d, i) => (
                          <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Box sx={{ width: 8, height: 8, borderRadius: "50%",
                                bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                              <Typography variant="caption" color="text.secondary">{d.name}</Typography>
                            </Stack>
                            <Typography variant="caption" fontWeight={700}>{fmt(d.value)}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </>
                  )
                }
              </Box>
            </Paper>
          </Stack>

          {/* ── Crédits debited/refunded + durées ──────────────────── */}
          {(totalDebited > 0 || netConsumed > 0) && (
            <Stack direction={{ xs:"column", md:"row" }} spacing={2} sx={{ mb: 2 }}>
              <Paper elevation={0} sx={{ flex: 1, border: "1px solid", borderColor: "divider",
                borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Typography variant="subtitle1" fontWeight={700}>Crédits consommés</Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    {[
                      { label: "Débités",        val: totalDebited,   color: "#ef4444" },
                      { label: "Remboursés",     val: totalRefunded,  color: "#22c55e" },
                      { label: "Net consommé",   val: netConsumed,    color: "#6366f1" },
                    ].map(({ label, val, color }) => (
                      <Box key={label} sx={{ flex: 1, textAlign: "center",
                        bgcolor: "#f8fafc", borderRadius: 2, py: 1.5, px: 1 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color }}>{fmt(val)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                          {label} (min)
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Box sx={{ height: 160 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[
                        { name: "Débités",    minutes: totalDebited   },
                        { name: "Remboursés", minutes: totalRefunded  },
                        { name: "Net",        minutes: netConsumed    },
                      ]} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }}
                          tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }}
                          tickLine={false} axisLine={false} />
                        <RTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                        <Bar dataKey="minutes" name="Minutes"
                          fill="#6366f1" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Paper>

              {/* Durées complétées vs annulées */}
              {(resStats?.completed_duration_minutes > 0 || resStats?.cancelled_duration_minutes > 0) && (
                <Paper elevation={0} sx={{ flex: 1, border: "1px solid", borderColor: "divider",
                  borderRadius: 3, overflow: "hidden" }}>
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Typography variant="subtitle1" fontWeight={700}>Durées (minutes)</Typography>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                      {[
                        { label: "Complétées", val: resStats?.completed_duration_minutes || 0, color: "#22c55e" },
                        { label: "Annulées",   val: resStats?.cancelled_duration_minutes  || 0, color: "#ef4444" },
                        { label: "Moyenne",    val: Math.round(resStats?.avg_duration_minutes || 0), color: "#6366f1" },
                      ].map(({ label, val, color }) => (
                        <Box key={label} sx={{ flex: 1, textAlign: "center",
                          bgcolor: "#f8fafc", borderRadius: 2, py: 1.5, px: 1 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ color }}>{fmt(val)}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{label}</Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Box sx={{ height: 160 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[
                          { name: "Complétées", minutes: resStats?.completed_duration_minutes || 0 },
                          { name: "Annulées",   minutes: resStats?.cancelled_duration_minutes || 0 },
                        ]} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }}
                            tickLine={false} axisLine={false} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }}
                            tickLine={false} axisLine={false} />
                          <RTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                          <Bar dataKey="minutes" name="Minutes" radius={[3, 3, 0, 0]}>
                            {[{ fill: "#22c55e" }, { fill: "#ef4444" }].map((e, i) => (
                              <Cell key={i} fill={e.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Stack>
          )}

          {/* ── Quotas + Pro-check ─────────────────────────────────── */}
          <SectionCard
            title={labId ? `Quotas — Lab #${labId}` : "Quotas & Simulateur"}
            subtitle={labId ? "" : "Sélectionne un lab pour voir ses quotas"}
            icon={<TimerIcon sx={{ fontSize: 18 }} />}
          >
            {labId && quotaLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {labId && !quotaLoading && quota && (
              <Stack direction={{ xs:"column", sm:"row" }} spacing={2} sx={{ mb: 2 }}>
                {[
                  { label: "Capacité",        val: fmtQuota(quota.capacity) },
                  { label: "Max/jour (min)",   val: fmtQuota(quota.max_daily_minutes_per_user) },
                  { label: "Max/semaine (min)",val: fmtQuota(quota.max_weekly_minutes_per_user) },
                  { label: "Réservations max", val: fmtQuota(quota.max_active_reservations_per_user) },
                ].map(({ label, val }) => (
                  <Box key={label} sx={{ flex: 1, bgcolor: "#f8fafc", borderRadius: 2,
                    p: 2, border: "1px solid #e2e8f0", textAlign: "center" }}>
                    <Typography variant="h6" fontWeight={700}>{val}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{label}</Typography>
                  </Box>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              {t("userStats.proCheck.title")}
            </Typography>
            <Stack direction={{ xs:"column", sm:"row" }} spacing={1.5} alignItems={{ sm:"center" }}>
              <TextField select size="small" label="Lab" value={labId}
                onChange={e => setLabId(e.target.value)} sx={{ minWidth: 200 }}>
                <MenuItem value="">Sélectionner un lab</MenuItem>
                {labsOptions.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
              </TextField>
              <TextField label="Début" type="datetime-local" size="small"
                InputLabelProps={{ shrink: true }} value={proStart}
                onChange={e => setProStart(e.target.value)} />
              <TextField label="Fin" type="datetime-local" size="small"
                InputLabelProps={{ shrink: true }} value={proEnd}
                onChange={e => setProEnd(e.target.value)} />
              <Button variant="contained" onClick={handleProCheck} disabled={proLoading}
                sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" }, whiteSpace: "nowrap" }}>
                {proLoading ? <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} /> : null}
                {proLoading ? "Vérification…" : t("userStats.proCheck.button")}
              </Button>
            </Stack>
            {proError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, fontSize: 12 }}>{proError}</Alert>}
            {proResult && (
              <Box sx={{ mt: 2, p: 2.5, borderRadius: 2,
                bgcolor: proResult.allowed ? "#f0fdf4" : "#fef2f2",
                border: `1px solid ${proResult.allowed ? "#86efac" : "#fca5a5"}` }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  {proResult.allowed
                    ? <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                    : <CancelIcon     sx={{ color: "#dc2626", fontSize: 18 }} />}
                  <Typography variant="subtitle2" fontWeight={700}
                    sx={{ color: proResult.allowed ? "#16a34a" : "#dc2626" }}>
                    {proResult.allowed ? t("userStats.proCheck.allowed") : t("userStats.proCheck.denied")}
                  </Typography>
                </Stack>
                {mapMsgs(t, proResult, "error").map((msg, i) => (
                  <Typography key={i} variant="caption" color="error.main" sx={{ display: "block", fontSize: 12 }}>
                    • {msg}
                  </Typography>
                ))}
                {mapMsgs(t, proResult, "warning").map((msg, i) => (
                  <Typography key={i} variant="caption" color="warning.main" sx={{ display: "block", fontSize: 12 }}>
                    ⚠ {msg}
                  </Typography>
                ))}
                {proResult.credits?.status === "OK" && (
                  <Typography variant="caption" color="success.main" sx={{ display: "block", mt: 0.5, fontSize: 12 }}>
                    Pack : {proResult.credits.lab_pack_code || "N/A"} —
                    avant : {proResult.credits.remaining_before ?? 0} min →
                    après : {proResult.credits.remaining_after ?? 0} min
                  </Typography>
                )}
              </Box>
            )}
          </SectionCard>

          {/* ── NextHop Academy packs ──────────────────────────────── */}
          {(nextHopPacks.length > 0 || nhError) && (
            <SectionCard
              title="Packs NextHop Academy"
              subtitle={`${fmt(nhTotal)} minutes attribuées au total`}
              icon={<SchoolIcon sx={{ fontSize: 18 }} />}>
              {nhError
                ? <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 12 }}>{nhError}</Alert>
                : (
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        {["Cours", "Minutes", "Source", "Date"].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11,
                            color: "text.secondary", textTransform: "uppercase",
                            letterSpacing: .4, borderBottom: "1px solid #e2e8f0" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {nextHopPacks.slice(0, 20).map((p, i) => (
                        <TableRow key={p?.external_id || i} sx={{
                          "&:hover": { bgcolor: "#f8fafc" },
                          "& td": { borderBottom: "1px solid #f1f5f9" },
                          "&:last-child td": { borderBottom: 0 },
                        }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {p?.label || `Cours #${p?.course_id ?? "-"}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              id: {p?.course_id ?? "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} sx={{ color: "#16a34a" }}>
                              +{fmt(Number(p?.minutes ?? p?.change_minutes ?? 0))} min
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {p?.source || "-"}{p?.site ? ` · ${p.site}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                            {p?.granted_at ? new Date(p.granted_at).toLocaleDateString("fr") : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              }
            </SectionCard>
          )}

          {/* ── Activité récente ───────────────────────────────────── */}
          {activity.length > 0 && (() => {
            const actTotal = activity.length;
            const actPages = Math.max(1, Math.ceil(actTotal / ACT_PAGE_SIZE));
            const actSlice = activity.slice(actPage * ACT_PAGE_SIZE, (actPage + 1) * ACT_PAGE_SIZE);
            return (
              <SectionCard
                title="Activité récente"
                subtitle={`${actTotal} événement${actTotal > 1 ? "s" : ""}`}
                icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
              >
                <Stack spacing={0}>
                  {actSlice.map((a, i) => (
                    <Box key={i} sx={{ py: 1.25, borderBottom: "1px solid #f1f5f9",
                      "&:last-child": { borderBottom: 0 } }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Chip size="small" label={a.level || "INFO"}
                          sx={{ height: 18, fontSize: 10, fontWeight: 700, flexShrink: 0,
                            bgcolor: a.level === "ERROR" ? "#fef2f2" : a.level === "WARNING" ? "#fffbeb" : "#f0f9ff",
                            color:   a.level === "ERROR" ? "#ef4444" : a.level === "WARNING" ? "#f59e0b" : "#0369a1" }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ display: "block", color: "text.primary", lineHeight: 1.5 }}>
                            {a.message}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                            {a.created_at ? new Date(a.created_at).toLocaleString("fr") : "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                {actTotal > ACT_PAGE_SIZE && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between"
                    sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f1f5f9" }}>
                    <Typography variant="caption" color="text.secondary">
                      {actPage * ACT_PAGE_SIZE + 1}–{Math.min((actPage + 1) * ACT_PAGE_SIZE, actTotal)} sur {actTotal}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" variant="outlined" disabled={actPage === 0}
                        onClick={() => setActPage(p => p - 1)}
                        sx={{ borderRadius: 2, fontSize: 11, minWidth: 32, px: 1 }}>←</Button>
                      {Array.from({ length: actPages }, (_, i) => i)
                        .slice(
                          Math.max(0, Math.min(actPage - 2, actPages - 5)),
                          Math.max(5, Math.min(actPage + 3, actPages))
                        )
                        .map(i => (
                          <Button key={i} size="small"
                            variant={i === actPage ? "contained" : "outlined"}
                            onClick={() => setActPage(i)}
                            sx={{
                              borderRadius: 2, fontSize: 11, minWidth: 32, px: 1,
                              ...(i === actPage ? {
                                bgcolor: "#6366f1", color: "#fff",
                                "&:hover": { bgcolor: "#4f46e5" },
                              } : {}),
                            }}>
                            {i + 1}
                          </Button>
                        ))
                      }
                      <Button size="small" variant="outlined" disabled={actPage >= actPages - 1}
                        onClick={() => setActPage(p => p + 1)}
                        sx={{ borderRadius: 2, fontSize: 11, minWidth: 32, px: 1 }}>→</Button>
                    </Stack>
                  </Stack>
                )}
              </SectionCard>
            );
          })()}
        </>
      )}
    </Box>
  );
}
